import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { applyFilters, DEFAULT_FILTERS, hasActiveFilters } from '../components/FilterPanel';
import SEOMeta from '../components/SEOMeta';
import { API_BASE } from '../config/api';

function CategoryPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState(() => {
    // Pre-apply brand filter when navigating from BrandStrip
    const presetBrand = location.state?.presetBrand;
    if (presetBrand) return { ...DEFAULT_FILTERS, brands: [presetBrand] };
    return DEFAULT_FILTERS;
  });
  const [searchQuery, setSearchQuery] = useState('');

useEffect(() => {
    fetch(`${API_BASE}/categories-with-products/`)
      .then(res => res.json())
      .then(data => {
        const found = data.find(c => c.slug === slug || String(c.id) === String(slug));
        if (!found) throw new Error('Not found');
        setCategory(found);
        setLoading(false);
        if (found.slug && found.slug !== slug) {
          navigate(`/category/${found.slug}`, { replace: true, state: location.state });
        }
      })
      .catch(() => {
        setError('Category not found.');
        setLoading(false);
      });
  }, [slug, navigate, location.state]);

  if (loading) return (
    <div className="category-page">
      <Header />
      <p className="status-msg">Loading...</p>
    </div>
  );

  if (error) return (
    <div className="category-page">
      <Header />
      <p className="status-msg error">{error} <Link to="/">Back to Home</Link></p>
    </div>
  );

  const liveResults = searchQuery.trim()
    ? category.products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 6)
    : [];

  const filterActive = hasActiveFilters(filters);
  const baseProducts = searchQuery.trim()
    ? category.products.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : category.products;
  const displayProducts = filterActive ? applyFilters(baseProducts, filters) : baseProducts;

  const categoryBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://herclesglobal.com/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://herclesglobal.com/category/${category.slug || category.id}` },
    ],
  };

  return (
    <div className="category-page">
      <SEOMeta
        title={`${category.name} – Buy Online in Kenya`}
        description={`Shop ${category.products.length}+ ${category.name} products in Kenya. Best prices, fast delivery across Nairobi and nationwide. Hercles Global – Kenya's trusted electronics store.`}
        canonical={`/category/${category.slug || category.id}`}
        robots={category.products.length === 0 ? 'noindex, follow' : undefined}
        jsonLd={categoryBreadcrumb}
      />
      <Header
        backTo="/" backLabel="← Back to Home"
        showSearch
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        liveSearchResults={liveResults}
        onFilterToggle={() => setFilterOpen(o => !o)}
        filterActive={filterActive}
        filterOpen={filterOpen}
        filterMode="category"
        filterProducts={category.products}
        filters={filters}
        onFiltersChange={setFilters}
      />

      <div className="category-page-inner">
        <div className="category-bar">
          <h1 className="category-bar-name">{category.name}</h1>
        </div>

        {displayProducts.length === 0 ? (
          <p className="status-msg" style={{ padding: '24px 0' }}>No products match your search or filters.</p>
        ) : (
          <div className="hp-product-grid">
            {displayProducts.map(p => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default CategoryPage;
